import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { FiGift, FiHome, FiMessageSquare, FiStar, FiUsers } from "react-icons/fi";
import { getAllMyMintSpaces, getSupportThreads } from "../api/mintSpace";
import MyCampaignsPage from "../pages/MyCampaignsPage";
import MintSpaceSupportPage from "../pages/MintSpaceSupportPage";
import MintSpaceAnnouncementsPage from "../pages/MintSpaceAnnouncementsPage";
import { useImageLoader } from "../hooks/useImageLoader";
import { useTranslation } from "../context/TranslationContext";
import { useSocket } from "../context/SocketContext";
import MintSpaceShell from "./MintSpaceShell";
import MintSpaceSidebarBannerHeader from "./MintSpaceSidebarBannerHeader";
import MintSpaceRatingStars from "./MintSpaceRatingStars";
import MintSpaceMembersPage from "../pages/MintSpaceMembersPage";
import MintSpaceNavButton from "./motion/MintSpaceNavButton";

const MintSpaceWindow = ({ isDark, setToast }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { socket } = useSocket();
  const [activeNav, setActiveNav] = useState("campaigns");
  const [mintSpace, setMintSpace] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hasMintSpace, setHasMintSpace] = useState(null);
  const [supportUnreadCount, setSupportUnreadCount] = useState(0);
  const containerRef = useRef(null);
  const { imagesLoaded } = useImageLoader(containerRef);

  const fetchMintSpace = async () => {
    try {
      const result = await getAllMyMintSpaces();
      if (result.success && result.data && result.data.length > 0) {
        setMintSpace(result.data[0]);
        setHasMintSpace(true);
      } else {
        setHasMintSpace(false);
      }
    } catch (error) {
      if (error.message?.includes("not found") || error.response?.status === 404) {
        setHasMintSpace(false);
      } else {
        setToast(t("toasts.errorLoadingMintSpace"));
        setHasMintSpace(false);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMintSpace();
  }, []);

  const isStaff = !!(mintSpace?.isOwner || mintSpace?.isModerator);

  useEffect(() => {
    if (!mintSpace?._id || !mintSpace.pages?.support?.enabled || !isStaff) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await getSupportThreads(mintSpace._id);
        if (
          !cancelled &&
          res.success &&
          res.data &&
          typeof res.data.totalUnread === "number"
        ) {
          setSupportUnreadCount(res.data.totalUnread);
        }
      } catch {
        /* ignore */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [mintSpace?._id, mintSpace?.pages?.support?.enabled, isStaff]);

  useEffect(() => {
    if (!socket || !mintSpace?._id || !mintSpace.pages?.support?.enabled || !isStaff) return;
    socket.emit("join_mintspace_staff", { mintSpaceId: mintSpace._id });
    const onBump = () => {
      getSupportThreads(mintSpace._id)
        .then((res) => {
          if (res.success && res.data && typeof res.data.totalUnread === "number") {
            setSupportUnreadCount(res.data.totalUnread);
          }
        })
        .catch(() => {});
    };
    socket.on("support_new_message", onBump);
    return () => {
      socket.off("support_new_message", onBump);
    };
  }, [socket, mintSpace?._id, mintSpace?.pages?.support?.enabled, isStaff]);

  const handleMintSpaceUpdate = (updatedSpace) => {
    setMintSpace(updatedSpace);
  };

  useEffect(() => {
    if (!mintSpace || activeNav !== "support") return;
    if (mintSpace.pages?.support?.enabled) return;
    setActiveNav("home");
  }, [mintSpace, activeNav]);

  const baseText = "text-label-primary";
  const mutedText = "text-label-secondary";
  const cardBase = "border-separator-default bg-bg-raised";

  const navItems = [
    { id: "campaigns", label: t("navigation.creatorRewards") || "Creator Rewards", icon: FiGift },
    { id: "home", label: t("navigation.home") || "Home", icon: FiHome },
    { id: "members", label: t("navigation.members") || "Members", icon: FiUsers },
    ...(mintSpace?.pages?.support?.enabled
      ? [
          {
            id: "support",
            label: t("navigation.supportChat") || "Support Chat",
            icon: FiMessageSquare,
            badge: isStaff ? supportUnreadCount : 0,
          },
        ]
      : []),
  ];

  if (loading || !imagesLoaded) {
    return (
      <div className="w-full h-full flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-accent-default border-r-transparent mb-4"></div>
          <div className={`text-lg ${mutedText}`}>{t("windows.loading") || "Loading..."}</div>
        </div>
      </div>
    );
  }

  if (hasMintSpace === false) {
    return (
      <div className="w-full h-full flex items-center justify-center min-h-[60vh]">
        <div className={`rounded-radius-lg border p-8 lg:p-12 max-w-2xl w-full mx-4 ${cardBase}`}>
          <div className="text-center">
            <div className="mb-6 flex justify-center">
              <div className="h-20 w-20 rounded-full bg-accent-default flex items-center justify-center shadow-lg">
                <FiStar className="h-10 w-10 text-accent-on" />
              </div>
            </div>
            <h2 className={`text-3xl font-bold mb-4 ${baseText}`}>
              {t("windows.createMintSpaceFirst") || "Create Your Mint Space First"}
            </h2>
            <p className={`text-lg mb-2 ${mutedText}`}>
              {t("windows.beforeAccessMintSpace") || "Before you can access your Mint Space, you need to create one."}
            </p>
            <p className={`text-base mb-8 ${mutedText}`}>
              {t("windows.mintSpaceDescription") ||
                "Your Mint Space is your dedicated workspace where you'll manage campaigns, announcements, and connect with creators."}
            </p>
            <button
              onClick={() => navigate("/create-business")}
              className="px-8 py-4 rounded-radius-sm bg-accent-default text-accent-on text-title-3 transition shadow-lg"
            >
              {t("windows.createMintSpace") || "Create Mint Space"}
            </button>
          </div>
        </div>
      </div>
    );
  }

  const sidebarHeader = (
    <MintSpaceSidebarBannerHeader
      isDark={isDark}
      mutedText={mutedText}
      title={mintSpace?.name || t("windows.mintSpace") || "Mint Space"}
      imageUrl={mintSpace?.logo}
      imageAlt={mintSpace?.name}
      placeholderLabel={mintSpace?.name ? "No Image" : undefined}
      onBack={() => navigate("/")}
      backAriaLabel={t("common.back") || "Back"}
    >
      {mintSpace?._id ? (
        <MintSpaceRatingStars
          mintSpaceId={mintSpace._id}
          isDark={isDark}
          isOwner={!!mintSpace?.isOwner}
          isMember={!!mintSpace?.isMember}
          compact
        />
      ) : null}
    </MintSpaceSidebarBannerHeader>
  );

  const sidebarNav = (
    <ul className="space-y-0.5">
      {navItems.map((item) => {
        const Icon = item.icon;
        const isActive = activeNav === item.id;
        return (
          <li key={item.id}>
            <MintSpaceNavButton
              isActive={isActive}
              isDark={isDark}
              layoutGroup="mint-hub-sidebar"
              onClick={() => setActiveNav(item.id)}
            >
              <Icon className="h-3.5 w-3.5 shrink-0 opacity-80" strokeWidth={2} />
              <span className="min-w-0 flex-1 truncate text-left">{item.label}</span>
              {item.badge > 0 && (
                <span className="flex h-4 min-w-[1.1rem] shrink-0 items-center justify-center rounded-md bg-rose-500 px-1 text-caption-1-semibold text-white">
                  {item.badge > 99 ? "99+" : item.badge}
                </span>
              )}
            </MintSpaceNavButton>
          </li>
        );
      })}
    </ul>
  );

  const mobileNav = navItems.map((item) => {
    const Icon = item.icon;
    const isActive = activeNav === item.id;
    return (
      <MintSpaceNavButton
        key={item.id}
        isActive={isActive}
        isDark={isDark}
        mobile
        layoutGroup="mint-hub-mobile"
        onClick={() => setActiveNav(item.id)}
      >
        <Icon className="h-3.5 w-3.5 shrink-0" />
        <span className="min-w-0 flex-1 truncate text-left text-caption-1-semibold">{item.label}</span>
        {item.badge > 0 && (
          <span className="flex h-4 min-w-[1.1rem] shrink-0 items-center justify-center rounded-full bg-red-500 px-1 text-caption-1-semibold text-white">
            {item.badge > 99 ? "99+" : item.badge}
          </span>
        )}
      </MintSpaceNavButton>
    );
  });

  const renderPanel = (navId) => {
    switch (navId) {
      case "campaigns":
        return (
          <div className="p-4 lg:p-6">
            <MyCampaignsPage isDark={isDark} setToast={setToast} />
          </div>
        );
      case "home":
        return (
          <div className="p-4 lg:p-6">
            <MintSpaceAnnouncementsPage
              isDark={isDark}
              setToast={setToast}
              isOwner={!!mintSpace?.isOwner}
              mintSpace={mintSpace}
              type="post"
              onNavigate={setActiveNav}
              onUpdate={handleMintSpaceUpdate}
            />
          </div>
        );
      case "support":
        if (!mintSpace?.pages?.support?.enabled) return null;
        return (
          <div className="p-4 lg:p-6">
            <MintSpaceSupportPage
              isDark={isDark}
              setToast={setToast}
              mintSpace={mintSpace}
              onSupportUnreadChange={setSupportUnreadCount}
              isMintSpaceOwner={mintSpace?.isOwner}
              isMintSpaceModerator={mintSpace?.isModerator}
            />
          </div>
        );
      case "members":
        if (!mintSpace?._id) return null;
        return (
          <div className="p-4 lg:p-6">
            <MintSpaceMembersPage
              isDark={isDark}
              setToast={setToast}
              isOwner={mintSpace?.isOwner}
              spaceId={mintSpace._id}
            />
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <MintSpaceShell
      ref={containerRef}
      isDark={isDark}
      sidebarHeader={sidebarHeader}
      sidebarNav={sidebarNav}
      mobileNav={mobileNav}
      mainTop={null}
      activePanelKey={activeNav}
      renderPanel={renderPanel}
    />
  );
};

export default MintSpaceWindow;
